import React from "react";

const Table = ({
  columns,
  data,
  loading = false,
  error = null,
  pagination = null,
  onPageChange,
  onPageSizeChange,
  maxHeight = "500px",
  emptyState = null,
}) => {
  const renderEmptyState = () => {
    if (emptyState) return emptyState;
    
    return (
      <tr>
        <td colSpan={columns.length} className="py-20 px-4">
          <div className="flex flex-col items-center justify-center text-center">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mb-4">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="12" y1="18" x2="12" y2="12"></line>
              <line x1="9" y1="15" x2="15" y2="15"></line>
            </svg>
            <h3 className="text-lg font-semibold text-ink mb-2">No Data Found</h3>
            <p className="text-sm text-muted max-w-md">No data available. Try adjusting your filters or check back later.</p>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="border border-line rounded-2xl overflow-hidden bg-white relative" style={{ maxHeight, display: 'flex', flexDirection: 'column' }}>
      <style>{`
        .table-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .table-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
        }
        .table-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }
        .table-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
      {loading ? (
        <div className="py-16 px-5 text-center">
          <div className="inline-block w-8 h-8 border-3 border-line border-t-indigo rounded-full animate-spin"></div>
        </div>
      ) : error ? (
        <div className="py-16 px-5 text-center text-red-600">{error}</div>
      ) : (
        <>
          <div className="overflow-x-auto overflow-y-auto flex-1 table-scrollbar">
            <table className="w-full border-collapse text-sm">
              <thead className="sticky top-0 bg-soft z-10">
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className={`py-2.5 px-4 text-xs font-semibold text-muted uppercase tracking-wider border-b border-line ${column.className || 'text-left'}`}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  renderEmptyState()
                ) : (
                  data.map((row, rowIndex) => (
                    <tr
                      key={row._id || rowIndex}
                      className={row.onClick ? "hover:bg-gray-50 cursor-pointer" : ""}
                      onClick={row.onClick}
                    >
                      {columns.map((column) => (
                        <td
                          key={column.key}
                          className={`py-2.5 px-4 border-b border-line ${column.cellClassName || ''}`}
                        >
                          {column.render ? column.render(row) : row[column.key]}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination && (
            <div 
              className="sticky bottom-0 flex justify-between items-center py-3.5 px-5 border-t border-line z-10"
              style={{
                background: 'rgba(248, 250, 252, 0.95)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)'
              }}
            >
              <div className="text-[13px] text-muted font-medium">
                Page {pagination.page} of {pagination.totalPages}
              </div>
              <div className="flex items-center gap-4">
                <select
                  value={pagination.limit}
                  onChange={(e) => onPageSizeChange(parseInt(e.target.value))}
                  className="py-1.5 px-3 border border-line rounded-lg bg-white text-[13px] font-medium cursor-pointer hover:bg-soft transition-colors"
                >
                  <option value={10}>10 / page</option>
                  <option value={25}>25 / page</option>
                  <option value={50}>50 / page</option>
                  <option value={100}>100 / page</option>
                </select>
                <div className="flex gap-2">
                  <button
                    onClick={() => onPageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="py-2 px-3.5 border border-line rounded-lg bg-white text-[13px] font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-soft transition-colors"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => onPageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.totalPages}
                    className="py-2 px-3.5 border border-line rounded-lg bg-white text-[13px] font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-soft transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Table;
